import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEffect } from "react";

export interface SiteSettings {
  original_price: string;
  sale_price: string;
  checkout_url: string;
  meta_pixel_id: string;
  meta_access_token: string;
}

const fetchSiteSettings = async (): Promise<SiteSettings> => {
  const { data, error } = await supabase
    .from("site_settings")
    .select("key, value");

  if (error) throw error;

  const settings: SiteSettings = {
    original_price: "$124.00",
    sale_price: "$14.00",
    checkout_url: "https://checkout.example.com",
    meta_pixel_id: "",
    meta_access_token: "",
  };

  data?.forEach((item) => {
    if (item.key in settings) {
      settings[item.key as keyof SiteSettings] = item.value;
    }
  });

  return settings;
};

export const useSiteSettings = () => {
  const queryClient = useQueryClient();

  // Subscribe to realtime changes
  useEffect(() => {
    const channel = supabase
      .channel('site-settings-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings',
        },
        () => {
          // Invalidate and refetch when any change occurs
          queryClient.invalidateQueries({ queryKey: ["site-settings"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return useQuery({
    queryKey: ["site-settings"],
    queryFn: fetchSiteSettings,
    staleTime: 1000 * 60, // 1 minute - data stays fresh longer since we have realtime
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
};

export const useUpdateSiteSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Partial<SiteSettings>) => {
      const updates = Object.entries(settings).map(([key, value]) =>
        supabase
          .from("site_settings")
          .update({ value })
          .eq("key", key)
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);
      
      if (errors.length > 0) {
        throw new Error("Failed to update some settings");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings"] });
      toast.success("Configurações salvas com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao salvar configurações: " + error.message);
    },
  });
};
